import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminLabManagement = ({ onNavigate, userInfo = {} }) => {
  // UI State
  const [isMobile, setIsMobile] = useState(false);
  const [activeSection, setActiveSection] = useState('workflow');

  // Data State
  const [departments, setDepartments] = useState([]);
  const [labs, setLabs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [categories, setCategories] = useState([]);

  // Loading & Error State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Admin Department Data (fetched from main admin endpoint like Adminstudentmanage.js)
  const [adminDepartments, setAdminDepartments] = useState([]);
  const [adminDepartmentsLoading, setAdminDepartmentsLoading] = useState(false);

  // Real Statistics State
  const [realStats, setRealStats] = useState({
    departments: 0,
    coordinators: 0,
    labs: 0,
    equipment: 0
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);

  // Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Refs for preventing re-render issues
  const searchInputRef = React.useRef(null);



  // Mobile Detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Admin API Helper (for main admin endpoints like /departments)
  const adminApiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // FETCH ADMIN DEPARTMENTS FROM DATABASE (same as Adminstudentmanage.js)
  const fetchAdminDepartments = async () => {
    try {
      setAdminDepartmentsLoading(true);
      const result = await adminApiCall('/departments');

      if (result.success) {
        setAdminDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching admin departments:', error);
    } finally {
      setAdminDepartmentsLoading(false);
    }
  };

  // Fetch Admin Departments on Mount
  useEffect(() => {
    fetchAdminDepartments();
  }, []);

  // Load data when section changes
  useEffect(() => {
    if (activeSection !== 'equipment' && (searchTerm || debouncedSearchTerm)) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }

    setCurrentPage(1);

    if (activeSection === 'workflow') {
      loadRealStatistics();
    } else {
      loadSectionData();
    }
  }, [activeSection]);

  // Handle pagination changes
  useEffect(() => {
    if (activeSection !== 'workflow') {
      loadSectionData();
    }
  }, [currentPage]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle debounced search changes
  useEffect(() => {
    if (activeSection === 'equipment') {
      setCurrentPage(1);
      loadSectionData();
    }
  }, [debouncedSearchTerm]);

  // API Helper
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_LINK}/api/admin/lab-management/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  };

  // Load Real Statistics
  const loadRealStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsRes, equipRes, deptRes, labRes, coordRes] = await Promise.allSettled([
        apiCall('stats'),
        apiCall('equipment?limit=1000&page=1'),
        apiCall('departments'),
        apiCall('labs'),
        apiCall('coordinators')
      ]);

      let realCounts = {
        departments: 0,
        coordinators: 0,
        labs: 0,
        equipment: 0
      };

      if (equipRes.status === 'fulfilled' && equipRes.value?.data) {
        const equipData = equipRes.value.data;
        realCounts.equipment = equipData.pagination?.totalCount ||
          equipData.equipment?.length ||
          0;
      }

      if (deptRes.status === 'fulfilled' && deptRes.value?.data?.departments) {
        realCounts.departments = deptRes.value.data.departments.length;
      }

      if (labRes.status === 'fulfilled' && labRes.value?.data?.labs) {
        realCounts.labs = labRes.value.data.labs.length;
      }

      if (coordRes.status === 'fulfilled' && coordRes.value?.data?.coordinators) {
        realCounts.coordinators = coordRes.value.data.coordinators.length;
      }

      if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
        const statsData = statsRes.value.data;
        if (realCounts.equipment === 0 && statsData.equipment > 0) {
          realCounts.equipment = statsData.equipment;
        }
        if (realCounts.departments === 0 && statsData.departments > 0) {
          realCounts.departments = statsData.departments;
        }
        if (realCounts.labs === 0 && statsData.labs > 0) {
          realCounts.labs = statsData.labs;
        }
        if (realCounts.coordinators === 0 && statsData.coordinators > 0) {
          realCounts.coordinators = statsData.coordinators;
        }
      }

      setRealStats(realCounts);
      setDataLoaded(true);

      if (realCounts.equipment === 0) {
        setError('No equipment found in database. Please check your database connection.');
      }

    } catch (error) {
      console.error('Failed to load statistics:', error);
      setError(`Failed to load statistics: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load Section Data
  const loadSectionData = useCallback(async () => {
    if (isSearching) return;

    setLoading(true);
    setError(null);

    try {
      let endpoint = '';
      let searchParam = '';

      switch (activeSection) {
        case 'departments':
          endpoint = 'departments';
          break;
        case 'coordinators':
          endpoint = 'coordinators';
          break;
        case 'labs':
          endpoint = 'labs';
          break;
        case 'equipment':
          if (debouncedSearchTerm) {
            setIsSearching(true);
            searchParam = `&search=${encodeURIComponent(debouncedSearchTerm)}`;
          }
          endpoint = `equipment?page=${currentPage}&limit=${limit}${searchParam}`;
          break;
        default:
          return;
      }

      const response = await apiCall(endpoint);

      if (response.success) {
        const dataKey = `${activeSection}`;
        const data = response.data[dataKey] || [];

        switch (activeSection) {
          case 'departments':
            setDepartments(data);
            setTotalCount(data.length);
            break;
          case 'coordinators':
            setCoordinators(data);
            setTotalCount(data.length);
            break;
          case 'labs':
            setLabs(data);
            setTotalCount(data.length);
            break;
          case 'equipment':
            setEquipment(data);
            if (response.data.pagination) {
              setTotalPages(response.data.pagination.totalPages);
              setTotalCount(response.data.pagination.totalCount);
            } else {
              setTotalCount(data.length);
            }
            break;
        }
      }
    } catch (error) {
      console.error(`Failed to load ${activeSection}:`, error);
      setError(`Failed to load ${activeSection}: ${error.message}`);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [activeSection, currentPage, limit, debouncedSearchTerm, isSearching]);

  // Load categories from backend
  const loadCategories = async () => {
    try {
      const response = await apiCall('equipment-categories');
      if (response.success && response.data.categories) {
        // Merge API categories with admin departments
        const apiCategories = response.data.categories;
        const deptLabels = adminDepartments.map(d => d.label);

        // Combine and remove duplicates
        const allCategories = [...new Set([...apiCategories, ...deptLabels])];
        setCategories(allCategories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Default categories + admin departments
      const defaultCategories = ['Computer', 'Microscope', 'Mechanical', 'Electrical', 'Chemical', 'Medical'];
      const deptLabels = adminDepartments.map(d => d.label);
      const allCategories = [...new Set([...defaultCategories, ...deptLabels])];
      setCategories(allCategories);
    }
  };

  // Handle Submit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let endpoint = '';
      let method = modalMode === 'edit' ? 'PUT' : 'POST';

      switch (modalType) {
        case 'department':
          endpoint = modalMode === 'edit' ? `departments/${selectedItem.id}` : 'departments';
          break;
        case 'coordinator':
          endpoint = modalMode === 'edit' ? `coordinators/${selectedItem.id}` : 'coordinators';
          break;
        case 'lab':
          endpoint = modalMode === 'edit' ? `labs/${selectedItem.id}` : 'labs';
          break;
        case 'equipment':
          endpoint = modalMode === 'edit' ? `equipment/${selectedItem.id}` : 'equipment';
          break;
        default:
          throw new Error('Invalid modal type');
      }

      await apiCall(endpoint, {
        method,
        body: JSON.stringify(formData)
      });

      setShowModal(false);

      if (activeSection === 'workflow') {
        await loadRealStatistics();
      } else {
        await loadSectionData();
      }

      alert(`${modalType} ${modalMode === 'edit' ? 'updated' : 'added'} successfully!`);
    } catch (error) {
      alert(`Failed to ${modalMode} ${modalType}: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete
  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      let endpoint = '';

      switch (type) {
        case 'department':
          endpoint = `departments/${id}`;
          break;
        case 'coordinator':
          endpoint = `coordinators/${id}`;
          break;
        case 'lab':
          endpoint = `labs/${id}`;
          break;
        case 'equipment':
          endpoint = `equipment/${id}`;
          break;
        default:
          throw new Error('Invalid type');
      }

      await apiCall(endpoint, { method: 'DELETE' });

      if (activeSection === 'workflow') {
        await loadRealStatistics();
      } else {
        await loadSectionData();
      }

      alert(`${type} deleted successfully!`);
    } catch (error) {
      alert(`Failed to delete ${type}: ${error.message}`);
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = getRequiredFields(modalType);

    requiredFields.forEach(field => {
      if (!formData[field]?.toString().trim() && formData[field] !== 0) {
        errors[field] = `${field.replace('_', ' ')} is required`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getRequiredFields = (type) => {
    switch (type) {
      case 'department': return ['id', 'name'];
      case 'coordinator': return ['lab_type', 'name', 'phone', 'email'];
      case 'lab': return ['id', 'name', 'department_id', 'type'];
      case 'equipment': return ['id', 'name', 'category', 'lab_id'];
      default: return [];
    }
  };



  // Open Modal
  const openModal = (type, mode, item = null) => {
    setModalType(type);
    setModalMode(mode);
    setSelectedItem(item);
    setFormErrors({});

    if (mode === 'edit' && item) {
      setFormData({ ...item });
    } else {
      setFormData({});
    }

    if (type === 'equipment') {
      loadCategories();
    }

    setShowModal(true);
  };

  // Workflow Guide Component
  const WorkflowGuide = () => (
    <div style={{ padding: '2rem' }}>
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '2rem' }}>🔬 Lab Management System</h2>
        <p style={{ margin: 0, fontSize: '1.1rem', opacity: 0.9 }}>
          Comprehensive management system for laboratory resources and equipment
        </p>
        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'rgba(220, 53, 69, 0.8)',
            borderRadius: '8px',
            fontSize: '0.9rem'
          }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Workflow Steps */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '2rem',
        marginBottom: '2rem'
      }}>
        {[
          {
            step: '1',
            title: 'Create Departments',
            desc: 'Set up lab departments and categories',
            icon: '🏢',
            action: () => setActiveSection('departments'),
            count: realStats.departments
          },
          {
            step: '2',
            title: 'Add Coordinators',
            desc: 'Assign coordinators to manage lab types',
            icon: '👨‍🔬',
            action: () => setActiveSection('coordinators'),
            count: realStats.coordinators
          },
          {
            step: '3',
            title: 'Setup Labs',
            desc: 'Create lab spaces under departments',
            icon: '🧪',
            action: () => setActiveSection('labs'),
            count: realStats.labs
          },
          {
            step: '4',
            title: 'Manage Equipment',
            desc: 'Add and track lab equipment inventory',
            icon: '🔬',
            action: () => setActiveSection('equipment'),
            count: realStats.equipment
          }
        ].map((item, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '2px solid transparent',
            position: 'relative'
          }}
            onClick={item.action}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.borderColor = '#238548';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'transparent';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
            }}>

            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#ffffff'
            }}>
              {item.step}
            </div>

            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
              {item.icon}
            </div>

            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.3rem' }}>
              {item.title}
            </h3>
            <p style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.95rem' }}>
              {item.desc}
            </p>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.5rem 1rem',
              backgroundColor: item.count > 0 ? '#e8f5e9' : '#fff3e0',
              color: item.count > 0 ? '#2e7d32' : '#f57c00',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: '600'
            }}>
              {loading ? '...' : `${item.count} items`}
            </div>
          </div>
        ))}
      </div>

      {/* Real Statistics Display */}
      <div style={{
        background: '#f8fafc',
        padding: '2rem',
        borderRadius: '12px',
        border: '2px dashed #238548'
      }}>
        <h3 style={{
          margin: '0 0 1.5rem 0',
          color: '#238548',
          textAlign: 'center',
          fontSize: '1.5rem'
        }}>
          📊 Live Database Statistics
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: '1.5rem'
        }}>
          {[
            { label: 'Departments', value: realStats.departments, icon: '🏢', color: '#3b82f6' },
            { label: 'Coordinators', value: realStats.coordinators, icon: '👨‍🔬', color: '#8b5cf6' },
            { label: 'Labs', value: realStats.labs, icon: '🧪', color: '#ec4899' },
            { label: 'Equipment', value: realStats.equipment, icon: '🔬', color: '#238548' }
          ].map((stat, index) => (
            <div key={index} style={{
              background: '#ffffff',
              padding: '1.5rem',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{stat.icon}</div>
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: stat.color,
                marginBottom: '0.25rem'
              }}>
                {loading ? '...' : stat.value}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Data Table Component
  const DataTable = ({ data, columns, type, onEdit, onDelete, onAdd, totalCount }) => (
    <div style={{
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '1.5rem 2rem',
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.5rem' }}>
            {type === 'equipment' ? '🔬 Equipment' :
              type === 'lab' ? '🧪 Labs' :
                type === 'coordinator' ? '👨‍🔬 Coordinators' : '🏢 Departments'}
          </h3>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
            {totalCount} total records
          </p>
        </div>

        {/* Search for Equipment */}
        {type === 'equipment' && (
          <div style={{ flex: isMobile ? '1 1 100%' : '0 0 auto', minWidth: isMobile ? '100%' : '300px' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                background: 'rgba(255,255,255,0.95)',
                color: '#333'
              }}
            />
          </div>
        )}

        <button
          onClick={onAdd}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#ffffff',
            color: '#238548',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          + Add New {type === 'equipment' ? 'Equipment' : type === 'coordinator' ? 'Coordinator' : type}
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p>Loading data...</p>
        </div>
      )}

      {/* Table */}
      {!loading && data.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.95rem'
            }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                  {columns.map((col, index) => (
                    <th key={index} style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      letterSpacing: '0.05em'
                    }}>
                      {col.header}
                    </th>
                  ))}
                  <th style={{
                    padding: '1rem 1.5rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    textTransform: 'uppercase',
                    fontSize: '0.8rem',
                    letterSpacing: '0.05em'
                  }}>
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, rowIndex) => (
                  <tr key={rowIndex} style={{
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background 0.2s ease'
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} style={{
                        padding: '1rem 1.5rem',
                        color: '#374151'
                      }}>
                        {col.render ? col.render(item[col.key], item) : item[col.key]}
                      </td>
                    ))}
                    <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => onEdit(item)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                          onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => onDelete(type, item.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#ef4444',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination for Equipment */}
          {type === 'equipment' && totalPages > 1 && (
            <div style={{
              padding: '1.5rem 2rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid #f1f5f9',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #e5e7eb',
                    background: currentPage === 1 ? '#f1f5f9' : '#ffffff',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    borderRadius: '6px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  ← Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #e5e7eb',
                    background: currentPage === totalPages ? '#f1f5f9' : '#ffffff',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151',
                    borderRadius: '6px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!loading && data.length === 0 && (
        <div style={{
          padding: '4rem 2rem',
          textAlign: 'center',
          color: '#9ca3af'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <p style={{ fontSize: '1.1rem', margin: '0 0 0.5rem 0' }}>No {type}s found</p>
          <p style={{ fontSize: '0.9rem', margin: 0 }}>
            {searchTerm ? 'Try adjusting your search' : `Click "Add New" to create your first ${type}`}
          </p>
        </div>
      )}
    </div>
  );

  const getColumns = (type) => {
    switch (type) {
      case 'department':
        return [
          { header: 'ID', key: 'id' },
          { header: 'Department Name', key: 'name' }
        ];

      case 'coordinator':
        return [
          { header: 'Lab Type', key: 'lab_type' },
          { header: 'Name', key: 'name' },
          { header: 'Phone', key: 'phone' },
          { header: 'Email', key: 'email' }
        ];

      case 'lab':
        return [
          { header: 'Lab ID', key: 'id' },
          { header: 'Lab Name', key: 'name' },
          { header: 'Department', key: 'department_id' },
          { header: 'Type', key: 'type' }
        ];

      case 'equipment':
        return [
          { header: 'Equipment ID', key: 'id' },
          { header: 'Name', key: 'name' },
          { header: 'Category', key: 'category' },
          { header: 'Lab', key: 'lab_id' },
          {
            header: 'Status',
            key: 'status',
            render: (value) => (
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '600',
                background: value === 'working' ? '#d1fae5' :
                  value === 'maintenance' ? '#fef3c7' : '#fee2e2',
                color: value === 'working' ? '#065f46' :
                  value === 'maintenance' ? '#92400e' : '#991b1b'
              }}>
                {value}
              </span>
            )
          }
        ];

      default:
        return [];
    }
  };

  // Modal Component - Memoized
  const Modal = useMemo(() => {
    if (!showModal) return null;

    const getModalTitle = () => {
      const action = modalMode === 'edit' ? 'Edit' : 'Add New';
      const entityName = modalType.charAt(0).toUpperCase() + modalType.slice(1);
      return `${action} ${entityName}`;
    };

    const handleInputChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const renderFormFields = () => {
      switch (modalType) {
        case 'department':
          return (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Department ID *
                </label>
                <input
                  type="text"
                  value={formData.id || ''}
                  onChange={(e) => handleInputChange('id', e.target.value)}
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.id ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: modalMode === 'edit' ? '#f3f4f6' : '#fff'
                  }}
                  placeholder="e.g., COMP, MECH, ECE"
                />
                {formErrors.id && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.id}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Department Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease'
                  }}
                  placeholder="e.g., Computer Science, Mechanical Engineering"
                />
                {formErrors.name && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.name}</p>}
              </div>
            </>
          );

        case 'coordinator':
          return (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Lab Type *
                </label>
                <select
                  value={formData.lab_type || ''}
                  onChange={(e) => handleInputChange('lab_type', e.target.value)}
                  disabled={adminDepartmentsLoading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.lab_type ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: adminDepartmentsLoading ? '#f9fafb' : '#fff',
                    cursor: adminDepartmentsLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">
                    {adminDepartmentsLoading ? 'Loading Departments...' : 'Select Lab Type'}
                  </option>
                  {adminDepartments.map(dept => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
                {formErrors.lab_type && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.lab_type}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Coordinator Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  placeholder="Full name of coordinator"
                />
                {formErrors.name && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.name}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.phone ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  placeholder="10-digit phone number"
                />
                {formErrors.phone && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.phone}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.email ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  placeholder="coordinator@example.com"
                />
                {formErrors.email && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.email}</p>}
              </div>
            </>
          );

        case 'lab':
          return (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Lab ID *
                </label>
                <input
                  type="text"
                  value={formData.id || ''}
                  onChange={(e) => handleInputChange('id', e.target.value)}
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.id ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: modalMode === 'edit' ? '#f3f4f6' : '#fff'
                  }}
                  placeholder="e.g., NND205, MSB102"
                />
                {formErrors.id && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.id}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Lab Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  placeholder="Lab name"
                />
                {formErrors.name && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.name}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Department *
                </label>
                <select
                  value={formData.department_id || ''}
                  onChange={(e) => handleInputChange('department_id', e.target.value)}
                  disabled={adminDepartmentsLoading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.department_id ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: adminDepartmentsLoading ? '#f9fafb' : '#fff',
                    cursor: adminDepartmentsLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">
                    {adminDepartmentsLoading ? 'Loading Departments...' : 'Select Department'}
                  </option>
                  {adminDepartments.map(dept => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
                {formErrors.department_id && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.department_id}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Lab Type *
                </label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  disabled={adminDepartmentsLoading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.type ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: adminDepartmentsLoading ? '#f9fafb' : '#fff',
                    cursor: adminDepartmentsLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">
                    {adminDepartmentsLoading ? 'Loading...' : 'Select Type'}
                  </option>
                  {adminDepartments.map(dept => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
                {formErrors.type && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.type}</p>}
              </div>
            </>
          );

        case 'equipment':
          return (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Equipment ID *
                </label>
                <input
                  type="text"
                  value={formData.id || ''}
                  onChange={(e) => handleInputChange('id', e.target.value)}
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.id ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: modalMode === 'edit' ? '#f3f4f6' : '#fff'
                  }}
                  placeholder="e.g., ECE132, PC-001"
                />
                {formErrors.id && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.id}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Equipment Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  placeholder="Equipment name"
                />
                {formErrors.name && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.name}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Category *
                </label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.category ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
                {formErrors.category && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.category}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Lab *
                </label>
                <select
                  value={formData.lab_id || ''}
                  onChange={(e) => handleInputChange('lab_id', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${formErrors.lab_id ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="">Select Lab</option>
                  {labs.map(lab => (
                    <option key={lab.id} value={lab.id}>{lab.name} ({lab.id})</option>
                  ))}
                </select>
                {formErrors.lab_id && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{formErrors.lab_id}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Status *
                </label>
                <select
                  value={formData.status || 'working'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="working">Working</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="not-working">Not Working</option>
                </select>
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '1rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
            color: '#ffffff',
            padding: '1.5rem 2rem',
            borderRadius: '12px 12px 0 0'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
              {getModalTitle()}
            </h3>
          </div>

          {/* Form */}
          <div style={{ padding: '2rem', overflowY: 'auto', flex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
              {renderFormFields()}
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '1.5rem 2rem',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
            backgroundColor: '#f8fafc'
          }}>
            <button
              onClick={() => setShowModal(false)}
              disabled={submitting}
              style={{
                padding: '0.75rem 1.5rem',
                border: '2px solid #e5e7eb',
                backgroundColor: '#ffffff',
                color: '#374151',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                fontWeight: '500',
                opacity: submitting ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '0.75rem 2rem',
                border: 'none',
                background: submitting
                  ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                  : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {submitting ? 'Saving...' : (modalMode === 'edit' ? 'Update' : 'Add')}
            </button>
          </div>
        </div>
      </div>
    );
  }, [showModal, modalType, modalMode, formData, formErrors, submitting, categories, labs, departments, adminDepartments, adminDepartmentsLoading]);

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-labs" userInfo={userInfo}>
      {/* Section Navigation */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          overflowX: 'auto',
          borderBottom: '1px solid #f1f5f9'
        }}>
          {[
            { key: 'workflow', label: '🎯 Workflow', icon: '🎯' },
            { key: 'departments', label: '🏢 Departments', icon: '🏢' },
            { key: 'coordinators', label: '👨‍🔬 Coordinators', icon: '👨‍🔬' },
            { key: 'labs', label: '🧪 Labs', icon: '🧪' },
            { key: 'equipment', label: '🔬 Equipment', icon: '🔬' }
          ].map((section, index) => (
            <button
              key={section.key}
              onClick={() => setActiveSection(section.key)}
              style={{
                flex: 1,
                minWidth: '140px',
                padding: '1rem 1.5rem',
                border: 'none',
                background: activeSection === section.key ?
                  'linear-gradient(135deg, #238548 0%, #97d6ac 100%)' : 'transparent',
                color: activeSection === section.key ? '#ffffff' : '#666',
                fontSize: '0.9rem',
                fontWeight: activeSection === section.key ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content based on active section */}
      {error && activeSection !== 'workflow' && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: 0 }}>⚠️ {error}</p>
        </div>
      )}

      {activeSection === 'workflow' && <WorkflowGuide />}
      {activeSection === 'departments' && (
        <DataTable
          data={departments}
          columns={getColumns('department')}
          type="department"
          onEdit={(item) => openModal('department', 'edit', item)}
          onDelete={handleDelete}
          onAdd={() => openModal('department', 'add')}
          totalCount={totalCount}
        />
      )}
      {activeSection === 'coordinators' && (
        <DataTable
          data={coordinators}
          columns={getColumns('coordinator')}
          type="coordinator"
          onEdit={(item) => openModal('coordinator', 'edit', item)}
          onDelete={handleDelete}
          onAdd={() => openModal('coordinator', 'add')}
          totalCount={totalCount}
        />
      )}
      {activeSection === 'labs' && (
        <DataTable
          data={labs}
          columns={getColumns('lab')}
          type="lab"
          onEdit={(item) => openModal('lab', 'edit', item)}
          onDelete={handleDelete}
          onAdd={() => openModal('lab', 'add')}
          totalCount={totalCount}
        />
      )}
      {activeSection === 'equipment' && (
        <DataTable
          data={equipment}
          columns={getColumns('equipment')}
          type="equipment"
          onEdit={(item) => openModal('equipment', 'edit', item)}
          onDelete={handleDelete}
          onAdd={() => openModal('equipment', 'add')}
          totalCount={totalCount}
        />
      )}

      {/* Modal */}
      {Modal}
    </Layout>
  );
};

export default AdminLabManagement;